import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap, type Schemas } from "./client";
import { reviewBatch, type ReviewDecision } from "../review";

// Clés de cache : tout ce qui concerne un projet commence par ["p", tenant, key] pour être invalidé d'un coup par le temps réel.
export const keys = {
  me: ["me"] as const,
  config: ["auth-config"] as const,
  projects: (tenant: string) => ["projects", tenant] as const,
  project: (tenant: string, project: string) => ["p", tenant, project] as const,
  credentials: (tenant: string) => ["credentials", tenant] as const,
};

type Scope = { tenant: string; project: string };
const path = (s: Scope) => ({ path: { tenant: s.tenant, project: s.project } });

export function useAuthConfig() {
  return useQuery({ queryKey: keys.config, queryFn: () => unwrap(api.GET("/auth/config")), staleTime: Infinity });
}

export function useMe() {
  return useQuery({ queryKey: keys.me, queryFn: () => unwrap(api.GET("/auth/me")), retry: false });
}

export function useProjects(tenant: string) {
  return useQuery({ queryKey: keys.projects(tenant), queryFn: () => unwrap(api.GET("/api/v1/t/{tenant}/projects", { params: { path: { tenant } } })) });
}

export function useWork(s: Scope) {
  return useQuery({
    queryKey: [...keys.project(s.tenant, s.project), "work"],
    queryFn: () => unwrap(api.GET("/api/v1/t/{tenant}/p/{project}/work", { params: path(s) })),
  });
}

export function useWorkDetails(s: Scope, key: string | null) {
  return useQuery({
    queryKey: [...keys.project(s.tenant, s.project), "work", key],
    queryFn: () => unwrap(api.GET("/api/v1/t/{tenant}/p/{project}/work/{key}", { params: { path: { ...s, key: key! } } })),
    enabled: key !== null,
  });
}

export function useKnowledge(s: Scope) {
  return useQuery({
    queryKey: [...keys.project(s.tenant, s.project), "knowledge"],
    queryFn: () => unwrap(api.GET("/api/v1/t/{tenant}/p/{project}/knowledge", { params: path(s) })),
  });
}

/** Recherche côté hub (accents ignorés, tous types, observations et éditions en attente comprises). */
export function useKnowledgeSearch(s: Scope, q: string) {
  return useQuery({
    queryKey: [...keys.project(s.tenant, s.project), "knowledge", "search", q],
    queryFn: () => unwrap(api.GET("/api/v1/t/{tenant}/p/{project}/knowledge/search", { params: { ...path(s), query: { q, limit: 50 } } })),
    enabled: q.trim().length > 0,
    placeholderData: (previous) => previous,
  });
}

/** Connaissance revue des autres projets du tenant et du brain d'organisation (PB-112) ; le projet courant est exclu. */
export function useTenantSearch(s: Scope, q: string) {
  return useQuery({
    queryKey: ["tenant", s.tenant, "knowledge", "search", s.project, q],
    queryFn: () => unwrap(api.GET("/api/v1/t/{tenant}/knowledge/search", { params: { path: { tenant: s.tenant }, query: { q, limit: 30, exclude: s.project } } })),
    enabled: q.trim().length > 0,
    placeholderData: (previous) => previous,
  });
}

/** Élément d'un autre projet du tenant (org:CNV-3, billing:DEC-12), lu à la demande. */
export function useTenantItem(tenant: string, reference: string | null) {
  return useQuery({
    queryKey: ["tenant", tenant, "knowledge", "item", reference],
    queryFn: () => unwrap(api.GET("/api/v1/t/{tenant}/knowledge/items/{reference}", { params: { path: { tenant, reference: reference! } } })),
    enabled: reference !== null,
    retry: false,
  });
}

/** Propose un élément canonique au brain d'organisation : une édition en attente du projet org (PB-112). */
export function useProposeToOrg(s: Scope) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ key, note }: { key: string; note: string | null }) =>
      unwrap(api.POST("/api/v1/t/{tenant}/p/{project}/knowledge/items/{key}/propose-org", { params: { path: { ...s, key } }, body: { note } })),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.me }),
  });
}

/** Versions d'un élément vues par le hub à chaque brain sync. */
export function useKnowledgeHistory(s: Scope, key: string | null) {
  return useQuery({
    queryKey: [...keys.project(s.tenant, s.project), "knowledge", "history", key],
    queryFn: () => unwrap(api.GET("/api/v1/t/{tenant}/p/{project}/knowledge/items/{key}/history", { params: { path: { ...s, key: key! } } })),
    enabled: key !== null,
    retry: false,
  });
}

/** Fil « appris récemment » : syncs, observations, éditions en attente, contestations, learnings de passation. */
export function useKnowledgeRecent(s: Scope, days = 30, limit = 100) {
  return useQuery({
    queryKey: [...keys.project(s.tenant, s.project), "knowledge", "recent", days, limit],
    queryFn: () => unwrap(api.GET("/api/v1/t/{tenant}/p/{project}/knowledge/recent", { params: { ...path(s), query: { days, limit } } })),
  });
}

/** Enregistre qu'une observation a été promue (le fichier .brain, lui, est écrit dans un clone par brain promote). */
export function useMarkPromoted(s: Scope) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ key, promotedTo }: { key: string; promotedTo: string }) =>
      unwrap(api.POST("/api/v1/t/{tenant}/p/{project}/observations/{key}/promoted", { params: { path: { ...s, key } }, body: { promoted_to: promotedTo } })),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.project(s.tenant, s.project) }),
  });
}

export function useObservations(s: Scope) {
  return useQuery({
    queryKey: [...keys.project(s.tenant, s.project), "observations"],
    queryFn: () => unwrap(api.GET("/api/v1/t/{tenant}/p/{project}/observations", { params: path(s) })),
  });
}

export function useSessions(s: Scope) {
  return useQuery({
    queryKey: [...keys.project(s.tenant, s.project), "sessions"],
    queryFn: () => unwrap(api.GET("/api/v1/t/{tenant}/p/{project}/sessions", { params: path(s) })),
    // La présence (actif, inactif) dépend du temps qui passe, pas seulement des événements.
    refetchInterval: 30_000,
  });
}

export function useEvents(s: Scope, tail = 60) {
  return useQuery({
    queryKey: [...keys.project(s.tenant, s.project), "events", tail],
    queryFn: () => unwrap(api.GET("/api/v1/t/{tenant}/p/{project}/events", { params: { ...path(s), query: { tail } } })),
  });
}

export function useMembers(s: Scope) {
  return useQuery({
    queryKey: [...keys.project(s.tenant, s.project), "members"],
    queryFn: () => unwrap(api.GET("/api/v1/t/{tenant}/p/{project}/members", { params: path(s) })),
  });
}

// Administration (PB-53)

const members = (s: Scope) => [...keys.project(s.tenant, s.project), "members"];
const invitations = (s: Scope) => [...keys.project(s.tenant, s.project), "invitations"];

export function useInvitations(s: Scope, enabled: boolean) {
  return useQuery({
    queryKey: invitations(s),
    queryFn: () => unwrap(api.GET("/api/v1/t/{tenant}/p/{project}/invitations", { params: path(s) })),
    enabled,
  });
}

export function useInvite(s: Scope) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { role: string; note?: string | null; days?: number | null }) =>
      unwrap(api.POST("/api/v1/t/{tenant}/p/{project}/invitations", { params: path(s), body })),
    onSuccess: () => client.invalidateQueries({ queryKey: invitations(s) }),
  });
}

export function useRevokeInvitation(s: Scope) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE("/api/v1/t/{tenant}/p/{project}/invitations/{id}", { params: { path: { ...path(s).path, id } } })),
    onSuccess: () => client.invalidateQueries({ queryKey: invitations(s) }),
  });
}

export function useChangeRole(s: Scope) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      unwrap(api.PATCH("/api/v1/t/{tenant}/p/{project}/members/{userId}", { params: { path: { ...path(s).path, userId } }, body: { role } })),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: members(s) });
      client.invalidateQueries({ queryKey: keys.me });
    },
  });
}

export function useRemoveMember(s: Scope) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => unwrap(api.DELETE("/api/v1/t/{tenant}/p/{project}/members/{userId}", { params: { path: { ...path(s).path, userId } } })),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: members(s) });
      client.invalidateQueries({ queryKey: keys.me });
    },
  });
}

export function useCreateProject(tenant: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { key: string; name: string; slug?: string | null }) => unwrap(api.POST("/api/v1/t/{tenant}/projects", { params: { path: { tenant } }, body })),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: keys.me });
      client.invalidateQueries({ queryKey: keys.projects(tenant) });
    },
  });
}

/** Invitation acceptée dans le navigateur : ouvre la session (credential humain) et renvoie les deux jetons, une seule fois. */
export function useAcceptInvitation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { code: string; display_name: string }) => unwrap(api.POST("/auth/invitation", { body: { ...body, machine: null } })),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.me }),
  });
}

export function useCredentials(tenant: string) {
  return useQuery({
    queryKey: keys.credentials(tenant),
    queryFn: () => unwrap(api.GET("/api/v1/t/{tenant}/credentials", { params: { path: { tenant } } })),
  });
}

export function useRevokeCredential(tenant: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE("/api/v1/t/{tenant}/credentials/{id}", { params: { path: { tenant, id } } })),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.credentials(tenant) }),
  });
}

export function useObservationFeedback(s: Scope) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ key, verdict }: { key: string; verdict: "confirm" | "dispute" }) =>
      unwrap(
        api.POST("/api/v1/t/{tenant}/p/{project}/observations/{key}/feedback", {
          params: { path: { ...s, key } },
          body: { session_id: null, verdict },
        }),
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.project(s.tenant, s.project) }),
  });
}

export function useCreateWork(s: Scope) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (item: Schemas["NewWorkItem"]) =>
      unwrap(api.POST("/api/v1/t/{tenant}/p/{project}/work", { params: path(s), body: { session_id: null, items: [item] } })),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.project(s.tenant, s.project) }),
  });
}

export function useForceRelease(s: Scope) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ key, reason }: { key: string; reason: string }) =>
      unwrap(api.POST("/api/v1/t/{tenant}/p/{project}/work/{key}/release", { params: { path: { ...s, key } }, body: { reason } })),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.project(s.tenant, s.project) }),
  });
}

export function useMarkDone(s: Scope) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ key, note }: { key: string; note: string | null }) =>
      unwrap(api.POST("/api/v1/t/{tenant}/p/{project}/work/{key}/done", { params: { path: { ...s, key } }, body: { note } })),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.project(s.tenant, s.project) }),
  });
}

export function useEditWork(s: Scope) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ key, body }: { key: string; body: Schemas["WorkEditRequest"] }) =>
      unwrap(api.PATCH("/api/v1/t/{tenant}/p/{project}/work/{key}", { params: { path: { ...s, key } }, body })),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.project(s.tenant, s.project) }),
  });
}

/** Santé de la mémoire : diagnostic du dernier brain sync, contestations, observations qui expirent. */
export function useKnowledgeHealth(s: Scope) {
  return useQuery({
    queryKey: [...keys.project(s.tenant, s.project), "health"],
    queryFn: () => unwrap(api.GET("/api/v1/t/{tenant}/p/{project}/knowledge/health", { params: path(s) })),
  });
}

/** Usage de la mémoire : briefings, éléments injectés, lus, déclarés utiles, prise en main (PB-113). */
export function useKnowledgeUsage(s: Scope, days = 30) {
  return useQuery({
    queryKey: [...keys.project(s.tenant, s.project), "usage", days],
    queryFn: () => unwrap(api.GET("/api/v1/t/{tenant}/p/{project}/knowledge/usage", { params: { ...path(s), query: { days } } })),
  });
}

/** Tâches closes dont les passations récentes attendent un résumé (PB-114). */
export function useCompactable(s: Scope) {
  return useQuery({
    queryKey: [...keys.project(s.tenant, s.project), "compactable"],
    queryFn: () => unwrap(api.GET("/api/v1/t/{tenant}/p/{project}/work/compactable", { params: path(s) })),
  });
}

/** Disputes d'éléments canoniques partagées par l'équipe (ouvertes). */
export function useDisputes(s: Scope) {
  return useQuery({
    queryKey: [...keys.project(s.tenant, s.project), "disputes"],
    queryFn: () => unwrap(api.GET("/api/v1/t/{tenant}/p/{project}/knowledge/disputes", { params: path(s) })),
  });
}

export function useResolveDispute(s: Scope) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, resolution }: { id: string; resolution: string }) =>
      unwrap(api.POST("/api/v1/t/{tenant}/p/{project}/knowledge/disputes/{id}/resolve", { params: { path: { ...s, id } }, body: { resolution } })),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.project(s.tenant, s.project) }),
  });
}

/**
 * Revue d'éditions en attente par un maintainer, une requête par élément : acceptée, une édition devient la mémoire du
 * projet ; refusée, elle n'est plus distribuée. Le résultat dit ce qui a réussi et ce qui a échoué.
 */
export function useReviewPending(s: Scope) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ itemIds, decision }: { itemIds: string[]; decision: ReviewDecision }) =>
      reviewBatch(itemIds, decision, (itemId) =>
        decision === "accept"
          ? unwrap(api.POST("/api/v1/t/{tenant}/p/{project}/knowledge/pending/{itemId}/accept", { params: { path: { ...s, itemId } } }))
          : unwrap(api.POST("/api/v1/t/{tenant}/p/{project}/knowledge/pending/{itemId}/reject", { params: { path: { ...s, itemId } } })),
      ),
    onSettled: () => client.invalidateQueries({ queryKey: keys.project(s.tenant, s.project) }),
  });
}

export type WorkStatusTarget = "ready" | "backlog" | "cancelled" | "reopen";

/** Changement de statut par un humain ; le hub applique la machine à états (annuler et rouvrir : maintainers). */
export function useChangeStatus(s: Scope) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ key, to, note }: { key: string; to: WorkStatusTarget; note: string | null }) =>
      unwrap(api.POST("/api/v1/t/{tenant}/p/{project}/work/{key}/status", { params: { path: { ...s, key } }, body: { to, note } })),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.project(s.tenant, s.project) }),
  });
}

/** Politique de claims et chemins protected (maintainers) : le snapshot des agents suit (PB-42). */
export function useUpdateProtection(s: Scope) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { claims_policy: string; protected_globs: string[] }) =>
      unwrap(api.PUT("/api/v1/t/{tenant}/p/{project}/settings", { params: path(s), body: { done_policy: null, ...body } })),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.me }),
  });
}

/** Connaissance lisible depuis les autres projets du tenant, ou gardée aux membres (PB-112, maintainers). */
export function useUpdateKnowledgeShared(s: Scope) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (shared: boolean) => unwrap(api.PUT("/api/v1/t/{tenant}/p/{project}/settings", { params: path(s), body: { done_policy: null, knowledge_shared: shared } })),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.me }),
  });
}

export type DonePolicy = "pull_request_merged" | "human" | "any";

export function useUpdateDonePolicy(s: Scope) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (donePolicy: DonePolicy) => unwrap(api.PUT("/api/v1/t/{tenant}/p/{project}/settings", { params: path(s), body: { done_policy: donePolicy } })),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.me }),
  });
}

/** Même règle que le hub (WorkItemStateMachine) : qui peut clore une tâche sans attendre le merge. */
export function canClose(role: string, policy: string): boolean {
  if (role === "viewer") return false;
  return policy === "pull_request_merged" ? role === "maintainer" || role === "owner" : true;
}

export function useLogout() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.POST("/auth/logout")),
    onSuccess: () => client.clear(),
  });
}

/** Brouillons de connaissance demandés depuis l'UI (PB-120) : brain apply les écrit dans .brain/, pour une PR. */
export function useDrafts(s: Scope) {
  return useQuery({
    queryKey: [...keys.project(s.tenant, s.project), "drafts"],
    queryFn: () => unwrap(api.GET("/api/v1/t/{tenant}/p/{project}/knowledge/drafts", { params: path(s) })),
  });
}

export type DraftInput = {
  action: "create" | "update" | "promote" | "confirm";
  ref?: string;
  type?: string;
  title?: string;
  summary?: string | null;
  body?: string | null;
  anchors?: string[] | null;
  fields?: Record<string, unknown> | null;
  status?: string | null;
};

export function useCreateDraft(s: Scope) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (draft: DraftInput) =>
      unwrap(
        api.POST("/api/v1/t/{tenant}/p/{project}/knowledge/drafts", {
          params: path(s),
          body: { ...draft, fields: (draft.fields ?? null) as Schemas["JsonObject"] | null },
        }),
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: [...keys.project(s.tenant, s.project), "drafts"] }),
  });
}

export function useCancelDraft(s: Scope) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE("/api/v1/t/{tenant}/p/{project}/knowledge/drafts/{id}", { params: { path: { ...s, id } } })),
    onSuccess: () => client.invalidateQueries({ queryKey: [...keys.project(s.tenant, s.project), "drafts"] }),
  });
}

/** Résumé d'une tâche close (PB-114), écrit depuis son détail : il remplace ses passations dans le briefing des agents. */
export function useSummarize(s: Scope) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ key, text }: { key: string; text: string }) =>
      unwrap(api.POST("/api/v1/t/{tenant}/p/{project}/work/{key}/summary", { params: { path: { ...s, key } }, body: { text } })),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.project(s.tenant, s.project) }),
  });
}

/**
 * Ce qui attend une décision humaine, compté de la même façon partout (barre latérale, Aujourd'hui, À revoir) :
 * observations vivantes, contestations ouvertes, éditions relues dans le hub (pas celles d'un dépôt git, relues dans
 * leur PR) et demandes de l'UI refusées par brain apply.
 */
export function useToReview(s: Scope) {
  const observations = useObservations(s);
  const disputes = useDisputes(s);
  const knowledge = useKnowledge(s);
  const drafts = useDrafts(s);
  const reviewedInHub = knowledge.data !== undefined && knowledge.data.knowledge_source !== "git";
  const counts = {
    observations: observations.data?.length ?? 0,
    promotable: (observations.data ?? []).filter((o) => o.promotion_ready && !o.promoted_to).length,
    disputes: (disputes.data ?? []).filter((d) => !d.resolved_at).length,
    edits: reviewedInHub ? (knowledge.data?.pending.length ?? 0) : 0,
    failedDrafts: (drafts.data ?? []).filter((d) => d.state === "failed").length,
  };
  return { ...counts, reviewedInHub, total: counts.observations + counts.disputes + counts.edits + counts.failedDrafts };
}

/** Dépôts de forge reliés au projet (PB-122) : pull requests et issues qui font avancer les tâches. */
export function useRepos(s: Scope) {
  return useQuery({
    queryKey: [...keys.project(s.tenant, s.project), "repos"],
    queryFn: () => unwrap(api.GET("/api/v1/t/{tenant}/p/{project}/repos", { params: path(s) })),
  });
}

export function useAddRepo(s: Scope) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { provider: string; full_name: string; default_branch: string; import_issues: string }) =>
      unwrap(api.POST("/api/v1/t/{tenant}/p/{project}/repos", { params: path(s), body })),
    onSuccess: () => client.invalidateQueries({ queryKey: [...keys.project(s.tenant, s.project), "repos"] }),
  });
}

/** Note d'un humain au prochain agent sur une tâche (PB-124) : lue en tête par l'agent qui la prend. */
export function useAddHumanNote(s: Scope) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ key, text }: { key: string; text: string }) =>
      unwrap(api.POST("/api/v1/t/{tenant}/p/{project}/work/{key}/notes", { params: { path: { ...s, key } }, body: { text } })),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.project(s.tenant, s.project) }),
  });
}

/** Page Organisation (PB-126) : brain d'organisation, propositions à relire, partage des projets. */
export function useOrg(tenant: string) {
  return useQuery({ queryKey: ["org", tenant], queryFn: () => unwrap(api.GET("/api/v1/t/{tenant}/org", { params: { path: { tenant } } })) });
}

export function useCreateOrg(tenant: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.POST("/api/v1/t/{tenant}/org", { params: { path: { tenant } } })),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["org", tenant] });
      void client.invalidateQueries({ queryKey: keys.me });
    },
  });
}
